import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Modal, Button, Card } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetBirthdays } from '../../../services/customerService';
import type { Birthday } from '../../../services/schemas/customerServiceSchemas';
import {
  BirthdayGreeting,
  BirthdayGreetingResult,
  getBirthdayGreetings,
  greetDirectorBirthday
} from '../../../services/birthdayGreetings';
import { describeBirthday, monthRange, nextSevenDaysRange, todayRange } from '../../../utils/birthdays';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../utils/rolePermissions';
import BirthdayCustomerModal from './BirthdayCustomerModal';
import ActionsMenu from '../../common/ActionsMenu';

type View = 'today' | 'week' | 'month';

const VIEWS: Array<{ id: View; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Next 7 days' },
  { id: 'month', label: 'By month' }
];

const PERSON_LABELS: Record<string, string> = {
  DIRECTOR: 'Director',
  INDIVIDUAL: 'Personal customer',
  REPRESENTATIVE: 'Representative'
};

const RESULT_MESSAGES: Record<BirthdayGreetingResult, string> = {
  SENT: 'Birthday SMS sent.',
  ALREADY_SENT: 'Already greeted today, so nothing was sent.',
  NO_NUMBER: 'No cell number of their own to send to: add one for this director.',
  SWITCHED_OFF: 'Birthday SMSes are switched off in Settings > Notifications, so nothing was sent.'
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Birthdays of the people behind active customers - directors, personal customers and
 * representatives - for today, the next seven days or a chosen month, with a button to SMS a
 * director their greeting. Greetings go by SMS only, never email (the business, 23 Oct 2026).
 */
const Birthdays: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (VIEWS.some(v => v.id === searchParams.get('view')) ? searchParams.get('view') : 'today') as View;
  const [month, setMonth] = useState(currentMonth());
  const { hasPermission } = usePermissions();
  const canSend = hasPermission(Permission.EDIT_CUSTOMER);
  const queryClient = useQueryClient();

  const range = useMemo(() => {
    if (view === 'today') return todayRange();
    if (view === 'week') return nextSevenDaysRange();
    const [y, m] = month.split('-').map(Number);
    return monthRange(y, m);
  }, [view, month]);

  const { data: birthdays, isLoading, isError } = useGetBirthdays(range, { query: { staleTime: 60_000 } });
  const { data: greetings } = useQuery({
    queryKey: ['birthday-greetings', range.from, range.to],
    queryFn: () => getBirthdayGreetings(range.from, range.to),
    staleTime: 60_000
  });

  const todayDates = useMemo(() => todayRange(), []);
  const weekDates = useMemo(() => nextSevenDaysRange(), []);
  const thisMonthDates = useMemo(() => {
    const now = new Date();
    return monthRange(now.getFullYear(), now.getMonth() + 1);
  }, []);
  const { data: todayBirthdays } = useGetBirthdays(todayDates, { query: { staleTime: 60_000 } });
  const { data: weekBirthdays } = useGetBirthdays(weekDates, { query: { staleTime: 60_000 } });
  const { data: monthBirthdays } = useGetBirthdays(thisMonthDates, { query: { staleTime: 60_000 } });
  const { data: todayGreetings } = useQuery({
    queryKey: ['birthday-greetings', todayDates.from, todayDates.to],
    queryFn: () => getBirthdayGreetings(todayDates.from, todayDates.to),
    staleTime: 60_000
  });

  const [viewing, setViewing] = useState<Birthday | null>(null);
  const [confirming, setConfirming] = useState<Birthday | null>(null);
  // Rows whose greeting is on its way. Sending takes a while (the mail server is slow), so it runs
  // in the background: the dialog closes at once and the rest of the page stays usable.
  const [sendingKeys, setSendingKeys] = useState<Set<string>>(new Set());
  const [outcomes, setOutcomes] = useState<Record<string, { message: string; ok: boolean }>>({});
  const rowKey = (b: Birthday) => `${b.personType}-${b.personId}-${b.birthday}`;

  /** The greeting already on record for a row: same customer, sent to the row's email or phone. */
  const greetingFor = (b: Birthday): BirthdayGreeting | undefined =>
    (greetings || []).find(g =>
      g.customerNumber === b.customerNumber
      && g.status === 'SENT'
      && (g.recipient === b.email || g.recipient === b.phone));

  const send = () => {
    const row = confirming;
    setConfirming(null);
    if (!row?.customerNumber) return;
    const key = rowKey(row);
    setSendingKeys(prev => new Set(prev).add(key));
    setOutcomes(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });

    greetDirectorBirthday(row.customerNumber, row.personId)
      .then(({ result }) => {
        setOutcomes(prev => ({ ...prev, [key]: { message: RESULT_MESSAGES[result], ok: result === 'SENT' } }));
        return queryClient.invalidateQueries({ queryKey: ['birthday-greetings'] });
      })
      .catch((e: any) => {
        setOutcomes(prev => ({
          ...prev,
          [key]: {
            message: e?.response?.data?.message || 'The SMS could not be sent. Try again in a moment.',
            ok: false
          }
        }));
      })
      .finally(() => {
        setSendingKeys(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      });
  };

  // People greeted today (by email or SMS), matched the same way the table matches a row.
  const greetedToday = (todayBirthdays || []).filter(b =>
    (todayGreetings || []).some(g =>
      g.customerNumber === b.customerNumber && g.status === 'SENT'
      && (g.recipient === b.email || g.recipient === b.phone))).length;

  const stats = [
    { label: 'Birthdays today', value: todayBirthdays?.length, icon: 'fa-birthday-cake', color: 'primary', border: 'rgba(0,123,255,0.2)', view: 'today' as View },
    { label: 'Next 7 days', value: weekBirthdays?.length, icon: 'fa-calendar', color: 'info', border: 'rgba(23,162,184,0.2)', view: 'week' as View },
    { label: 'This month', value: monthBirthdays?.length, icon: 'fa-calendar-check-o', color: 'warning', border: 'rgba(255,193,7,0.3)', view: 'month' as View },
    {
      label: 'Greeted today',
      value: todayBirthdays ? `${greetedToday} of ${todayBirthdays.length}` : undefined,
      icon: 'fa-envelope-open',
      color: 'success',
      border: 'rgba(40,167,69,0.2)',
      view: 'today' as View
    }
  ];

  const setView = (next: View) => setSearchParams(next === 'today' ? {} : { view: next });

  const sendDisabledReason = (b: Birthday): string | null => {
    if (!canSend) return 'You do not have permission to message customers';
    if (b.personType !== 'DIRECTOR') return 'Greetings for personal customers and representatives are not available yet';
    if (!b.phone) return 'No cell number on record';
    return null;
  };

  return (
    <div>
      {/* Header - the same green banner as Leads */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/loan-customers">Customers</Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">Birthdays</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="fs-2 fw-bold mb-2 text-white">Birthdays</h1>
              <p className="mb-0 text-white fs-6 opacity-75">
                Celebrate the people behind our clients: see whose birthday is coming up and send them a greeting
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {/* Summary cards - each opens its period in the table below */}
          <div className="row g-4 mb-4">
            {stats.map(card => (
              <div key={card.label} className="col-lg-3 col-md-6">
                <Card
                  className={`border-0 shadow-sm h-100 ${view === card.view && card.label !== 'Greeted today' ? 'border-start border-4 border-primary' : ''}`}
                  role="button"
                  onClick={() => setView(card.view)}
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">{card.label}</p>
                        <h3 className={`mb-0 fw-bold text-${card.color}`}>
                          {card.value ?? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                        </h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: `1px solid ${card.border}` }}>
                        <i className={`fa ${card.icon} fa-2x text-${card.color}`} style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
              <h6 className="card-title mb-0">
                <i className="fa fa-birthday-cake me-2 text-primary"></i>
                Client birthdays
              </h6>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div className="btn-group" role="group" aria-label="Birthday period">
                  {VIEWS.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      className={`btn btn-sm ${view === v.id ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setView(v.id)}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                {view === 'month' && (
                  <input
                    id="birthday-month"
                    type="month"
                    className="form-control form-control-sm"
                    style={{ width: 170 }}
                    value={month}
                    aria-label="Month"
                    onChange={e => e.target.value && setMonth(e.target.value)}
                  />
                )}
              </div>
            </div>

            <div className="card-body">
              <p className="text-muted small mb-3">
                Directors of active business customers, personal customers and business
                representatives. The date of birth is the one captured, else it is read from the SA ID
                number; people with neither are not listed.
              </p>

              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
                </div>
              ) : isError ? (
                <div className="alert alert-danger mb-0">Birthdays could not be loaded. Refresh to try again.</div>
              ) : !birthdays?.length ? (
                <div className="text-center text-muted py-5">
                  <i className="fa fa-birthday-cake fa-2x mb-2 d-block"></i>
                  {view === 'today' ? 'No client birthdays today.' : 'No client birthdays in this period.'}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Birthday</th>
                        <th>Name</th>
                        <th>Customer</th>
                        <th className="text-end">Turning</th>
                        <th>Cell phone</th>
                        <th>Greeting</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {birthdays.map(b => {
                        const greeting = greetingFor(b);
                        const reason = sendDisabledReason(b);
                        const key = rowKey(b);
                        const isSending = sendingKeys.has(key);
                        const outcome = outcomes[key];
                        return (
                          <tr key={key}>
                            <td className="text-nowrap">
                              <div className={describeBirthday(b.birthday) === 'Today' ? 'fw-semibold text-primary' : ''}>
                                {describeBirthday(b.birthday)}
                              </div>
                              {view !== 'today' && <small className="text-muted">{b.birthday}</small>}
                            </td>
                            <td>
                              <div className="fw-semibold">{b.name}</div>
                              <small className="text-muted">
                                {PERSON_LABELS[b.personType] || b.personType}
                                {b.dateOfBirthSource === 'ID_NUMBER' && ' · date from ID number'}
                              </small>
                            </td>
                            <td>
                              {b.customerId ? (
                                <Link to={`/loan-customers/${b.customerId}/view`}>{b.companyName || b.customerNumber}</Link>
                              ) : (b.companyName || b.customerNumber)}
                              {b.companyName && <div><small className="text-muted">{b.customerNumber}</small></div>}
                            </td>
                            <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.turningAge}</td>
                            <td className="text-nowrap">
                              {b.phone ? (
                                <a href={`tel:${b.phone.replace(/\s+/g, '')}`} className="text-decoration-none">
                                  <i className="fa fa-mobile text-success me-1"></i>{b.phone}
                                </a>
                              ) : <span className="text-muted">None on record</span>}
                            </td>
                            <td>
                              {isSending ? (
                                <span className="text-muted small text-nowrap">
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Sending…
                                </span>
                              ) : greeting ? (
                                <span className="badge bg-success-subtle text-success-emphasis" title={greeting.recipient}>
                                  <i className="fa fa-check me-1"></i>
                                  Greeted{greeting.sentAt ? ` ${new Date(greeting.sentAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                                  {greeting.channel === 'SMS' ? ' (SMS)' : ''}
                                </span>
                              ) : (
                                <span className="badge bg-light text-muted border">Not yet</span>
                              )}
                              {outcome && !isSending && !(outcome.ok && greeting) && (
                                <div className={`small mt-1 text-wrap ${outcome.ok ? 'text-success' : 'text-warning-emphasis'}`} style={{ maxWidth: 260 }}>
                                  {outcome.message}
                                </div>
                              )}
                            </td>
                            <td className="text-end">
                              <ActionsMenu
                                items={[
                                  { label: 'View customer', icon: 'fa-eye', onClick: () => setViewing(b) },
                                  {
                                    label: 'Send birthday SMS',
                                    icon: 'fa-comment',
                                    onClick: () => setConfirming(b),
                                    disabled: !!reason || !!greeting || isSending,
                                    // Say why it cannot be sent, rather than leaving a greyed-out item unexplained.
                                    hint: greeting ? 'Already greeted' : isSending ? 'Sending…' : reason || undefined
                                  }
                                ]}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <BirthdayCustomerModal birthday={viewing} onHide={() => setViewing(null)} />

      <Modal show={!!confirming} onHide={() => setConfirming(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Send birthday SMS?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirming && (
            <p className="mb-0">
              Tirhelo's birthday greeting will be sent by SMS to <strong>{confirming.name}</strong>
              {confirming.companyName ? <> of {confirming.companyName}</> : null}.
              It goes to their own cell number only.
              Sending can take a little while; you can carry on working and the row updates when it is done.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setConfirming(null)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={send}>
            Send SMS
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Birthdays;
